import { Auth } from 'aws-amplify'
import { IUser } from '../types/interfaces/IUser'
import { AUTH_USER_TOKEN_KEY } from '../constants/auth'
import { cryptoService } from './crypto.service'
import {
  GetFanzaUser,
  getFanzaUser,
  getSfUser,
} from 'store/api-slices/userSlice'
import { store } from 'store/store'
import {
  setForm,
  //  setIsLayoutLoading
} from 'store/reducers/layoutReducer'
import { FormTypes } from 'types/enums/EFormTypes'
import { clearAuthCookies } from 'shared/helpers/loginUtils'
import { setToken } from 'store/reducers/userReducer'
import { UserAuth } from 'types/types/AuthProps'

type SignUpParameters = {
  username: string
  password: string
}

export const signUp = async ({
  username,
  password,
}: SignUpParameters): Promise<IUser | null | { error: string }> => {
  try {
    const signUpParams = {
      username: cryptoService.getHashedValue(username.toLowerCase()),
      password,
      attributes: {
        email: username,
      },
      autoSignIn: {
        enabled: true,
      },
    }

    await Auth.signUp(signUpParams)

    return {
      isAuthenticated: false,
    }
  } catch (err: unknown) {
    console.log('error signing up:', err)
    const error = err as Error
    if (error.message.startsWith('User already exists')) {
      return {
        error: 'UserNameExists',
      }
    }
    return null
  }
}

export const resendConfirmSignUp = async (username: string): Promise<void> => {
  try {
    await Auth.resendSignUp(
      cryptoService.getHashedValue(username.toLowerCase()),
    )
  } catch (err) {
    console.log('error resending code: ', err)
  }
}

export const confirmSignUp = async (
  username: string,
  code: string,
): Promise<boolean> => {
  try {
    await Auth.confirmSignUp(
      cryptoService.getHashedValue(username.toLowerCase()),
      code,
    )
    return true
  } catch (error) {
    console.log('error confirming sign up', error)
    return false
  }
}

export const signIn = async (
  username: string,
  password: string,
): Promise<
  | {
      isAuthenticated: boolean
      email: string
      token: string
    }
  | null
  | { error: string }
> => {
  try {
    const signedUser = await Auth.signIn({
      username: cryptoService.getHashedValue(username.toLowerCase()),
      password,
    })
    store.dispatch(setToken(signedUser.signInUserSession.idToken.jwtToken))
    const user = {
      isAuthenticated: true,
      email: username,
      token: signedUser.signInUserSession.idToken.jwtToken,
    }
    return user
  } catch (err) {
    const error = err as Error
    if (error.message.startsWith('User is not confirmed')) {
      return {
        error: 'UserNotConfirmed',
      }
    }
    console.log('error signing in: ', err)
    return null
  }
}

export const updatePreferredUsername = async (
  newUsername: string,
): Promise<boolean> => {
  try {
    const user = await Auth.currentAuthenticatedUser()
    await Auth.updateUserAttributes(user, {
      preferred_username: cryptoService.getHashedValue(newUsername),
    })

    return true
  } catch (error: unknown) {
    console.log('error updating preferred_username: ', error)
    throw error
  }
}

export const updatePhoneNumber = async (phoneNum: string): Promise<boolean> => {
  try {
    const user = await Auth.currentAuthenticatedUser()
    await Auth.updateUserAttributes(user, {
      phone_number: '+972' + phoneNum.slice(1),
    })
    return true
  } catch (error: unknown) {
    console.log('error updating phone_number: ', error)
    throw error
  }
}

export const resetPassword = async (username: string): Promise<void> => {
  try {
    const user = await Auth.forgotPassword(
      cryptoService.getHashedValue(username.toLowerCase()),
    )
    return user
  } catch (error) {
    console.log('error reset password: ', error)
    throw error
  }
}

export const resetPasswordSubmit = async (
  username: string,
  code: string,
  password: string,
): Promise<boolean> => {
  try {
    await Auth.forgotPasswordSubmit(
      cryptoService.getHashedValue(username.toLowerCase()),
      code,
      password,
    )
    return true
  } catch (error: unknown) {
    console.log('error forgot password submit: ', error)
    throw error
  }
}

const fetchUserDetails = async () => {
  try {
    const { data, error } = await store.dispatch(getSfUser.initiate({}))
    if (error) throw error
    return { success: data ? true : false, data }
  } catch (error) {
    console.error('Error fetching user details:', error)
    throw error
  }
}

export const isUserAuthenticatedDetails = async (): Promise<UserAuth> => {
  const idToken = localStorage.getItem(AUTH_USER_TOKEN_KEY)

  if (!idToken) {
    store.dispatch(setForm({ formType: FormTypes.LOGIN_FORM }))
    return { isAuthenticated: false }
  }

  try {
    const { success, data } = await fetchUserDetails()
    if (!success) throw new Error()

    const email = data?.email || ''

    store.dispatch(setToken(idToken))

    return {
      isAuthenticated: true,
      ...(email && { email }),
      toptixId: data?.toptixId,
      newsletter: data?.newsletter,
      seenMarketingMessage: data?.personalDetails?.seenMarketingMessage,
    }
  } catch (error) {
    console.error('Error parsing idToken:', error)
    store.dispatch(setToken())
    store.dispatch(setForm({ formType: FormTypes.LOGIN_FORM }))
    return { isAuthenticated: false }
  }
}

export const isFanzaUserAuthenticatedDetails = async (): Promise<
  GetFanzaUser | undefined
> => {
  const idToken = localStorage.getItem(AUTH_USER_TOKEN_KEY)

  if (!idToken) {
    store.dispatch(setForm({ formType: FormTypes.LOGIN_FORM }))
    return
  }

  try {
    const { data } = await store.dispatch(getFanzaUser.initiate())
    if (!data) throw new Error()
    return data
  } catch (error) {
    console.error('Error parsing idToken:', error)
    store.dispatch(setForm({ formType: FormTypes.LOGIN_FORM }))
    throw error
  }
}

export const getToken = () => {
  return localStorage.getItem(AUTH_USER_TOKEN_KEY)
}

export const logout = () => {
  localStorage.clear()
  store.dispatch(setToken())
  clearAuthCookies()

  if (window?.opener) {
    window.close()
  }
}
